import { useSeo } from "../lib/seo";
import { PageHero } from "../components/PageHero";
import { Frame, Lines, Reveal } from "../components/motion";
import { Eyebrow, Cta } from "../components/ui-bits";
import { CONTACT } from "../data/site";
import { ContactCta } from "../components/ContactCta";

const LEADS = [
  {
    id: "irene",
    first: "Irene",
    name: "Irene Borghi",
    role: "Fondatrice · Strategia e marketing",
    img: "/img/irene-2.webp",
    pos: "object-[50%_30%]",
    text: "Ha fondato B Digital Studio nel 2022, dopo un percorso nel mondo dei contenuti digitali, del marketing e della creazione di community online. Segue la direzione strategica dei progetti e il dialogo con le attività che scelgono lo studio.",
    quote: "Mi piace lavorare con chi ha un progetto in cui crede davvero. Poi troviamo insieme il modo di farlo conoscere.",
    booking: CONTACT.bookingIrene,
  },
  {
    id: "giulia",
    first: "Giulia",
    name: "Giulia Borghi",
    role: "Comunicazione strategica e video",
    img: "/img/giulia-crop.webp",
    pos: "object-[50%_35%]",
    text: "Lavora al fianco di Irene e segue da vicino tutti i progetti dello studio. Il suo focus è la comunicazione strategica, con un'attenzione particolare al video: dall'idea al formato, fino al modo in cui un contenuto viene raccontato.",
    quote: "Cerco sempre l'equilibrio tra quello che il cliente immagina e quello che il suo pubblico ha bisogno di vedere.",
    booking: CONTACT.bookingGiulia,
  },
];

const AREAS = ["Como", "Monza e Brianza", "Varese", "Lecco"];

const NETWORK = [
  { t: "Grafica", d: "Caroselli, post e materiali visivi coerenti con l'identità del brand." },
  { t: "Fotografi", d: "Diversi fotografi, scelti in base al progetto e al tipo di shooting." },
  { t: "Videomaker", d: "Riprese e montaggio di Reel, video parlati, interviste e contenuti promozionali." },
  { t: "ADV specialist", d: "Pianificazione e gestione delle campagne su Meta." },
  { t: "Newsletter specialist", d: "Progettazione e scrittura delle comunicazioni via email." },
];

export default function Studio() {
  useSeo("Chi siamo: studio creativo in Lombardia e la fondatrice Irene Borghi", "La storia di B Digital Studio, studio di comunicazione digitale tra Como, Monza e Brianza, Varese e Lecco: la visione della fondatrice Irene Borghi, il team e il metodo.");
  return (
    <>
      <PageHero
        testid="studio-hero"
        eyebrow="Studio"
        lines={["Non siamo nati", "per pubblicare post."]}
        sublines={["Siamo nati per costruire", "comunicazione."]}
        intro="Studio creativo di comunicazione digitale con base in Lombardia. Lavoriamo con aziende, professionisti e attività locali tra le province di Como, Monza e Brianza, Varese e Lecco."
      />
      <section className="px-5 pb-24 sm:px-10 md:pb-36 lg:px-16" data-testid="studio-story">
        <div className="grid grid-cols-12 gap-y-14 lg:gap-x-10">
          <Frame src="/img/set-produzione.webp" alt="Produzione video con smartphone su stabilizzatore e fotocamera" className="col-span-12 aspect-[16/10] lg:col-span-7 lg:self-start" priority />
          <div className="col-span-12 lg:col-span-4 lg:col-start-9">
            <Reveal><Eyebrow n="01">La storia</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <p className="mt-8 text-base leading-relaxed text-ink/80 md:text-lg">
                B Digital Studio nasce nel 2022 da un progetto di Irene Borghi, dopo un percorso nel mondo dei contenuti digitali, del marketing e della creazione di community online.
              </p>
              <p className="mt-5 text-base leading-relaxed text-ink/80 md:text-lg">
                Un'esperienza che ha portato a una consapevolezza: per comunicare bene non basta conoscere i social. Bisogna comprendere le persone, i comportamenti, il mercato e soprattutto le aziende che scegliamo di raccontare.
              </p>
              <p className="mt-5 text-base leading-relaxed text-ink/80 md:text-lg">
                Oggi B Digital Studio riunisce competenze diverse, dalla strategia alla fotografia, dalla produzione video al graphic design, fino alla gestione delle campagne pubblicitarie.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-white/60 px-5 py-24 sm:px-10 md:py-36 lg:px-16" data-testid="studio-founder">
        <div className="grid grid-cols-12 items-end gap-y-8 lg:gap-x-10">
          <div className="col-span-12 lg:col-span-3"><Reveal><Eyebrow n="02">Chi guida lo studio</Eyebrow></Reveal></div>
          <div className="col-span-12 lg:col-span-9">
            <Lines lines={["Due persone,", "un unico sguardo sul progetto."]} className="text-4xl leading-[1.1] sm:text-5xl lg:text-6xl" />
          </div>
        </div>
        <div className="mt-16 grid grid-cols-1 gap-x-10 gap-y-20 md:mt-24 md:grid-cols-2 lg:gap-x-16">
          {LEADS.map((p) => (
            <article key={p.name} data-testid={`lead-${p.id}`}>
              <Frame src={p.img} alt={`${p.name}, B Digital Studio`} className="aspect-[4/5] w-full" imgClass={p.pos} parallax={5} testid={`${p.id}-portrait`} />
              <Reveal className="mt-10">
                <p className="eyebrow">{p.role}</p>
                <h3 className="mt-4 text-4xl md:text-5xl">{p.name}</h3>
                <p className="mt-6 text-base leading-relaxed text-ink/80 md:text-lg">{p.text}</p>
                {p.quote && (
                  <blockquote className="mt-8 border-l border-teal pl-5 font-serif text-xl leading-snug md:text-2xl" data-testid={`${p.id}-quote`}>“{p.quote}”</blockquote>
                )}
                <div className="mt-8">
                  <Cta href={p.booking} target="_blank" rel="noopener noreferrer" variant="outline" testid={`${p.id}-booking`}>20 minuti video call con {p.first}</Cta>
                </div>
              </Reveal>
            </article>
          ))}
        </div>
      </section>

      <section className="px-5 py-24 sm:px-10 md:py-36 lg:px-16" data-testid="studio-team">
        <div className="grid grid-cols-12 items-end gap-y-8 lg:gap-x-10">
          <div className="col-span-12 lg:col-span-3"><Reveal><Eyebrow n="03">Il team</Eyebrow></Reveal></div>
          <div className="col-span-12 lg:col-span-9">
            <Lines lines={["Le persone dietro", "ogni progetto."]} className="text-4xl leading-[1.1] sm:text-5xl lg:text-6xl" />
          </div>
        </div>
        <div className="mt-16 grid grid-cols-12 gap-y-10 border-t border-ink/15 pt-12 md:mt-24 lg:gap-x-10" data-testid="team-network">
          <div className="col-span-12 lg:col-span-4">
            <Reveal>
              <p className="font-serif text-2xl leading-snug md:text-3xl">Intorno a noi, una rete di professionisti.</p>
              <p className="mt-5 text-base leading-relaxed text-ink/75">
                A seconda del progetto coinvolgiamo le figure giuste, coordinate dallo studio dall'inizio alla fine.
              </p>
            </Reveal>
          </div>
          <ul className="col-span-12 lg:col-span-7 lg:col-start-6">
            {NETWORK.map((r, i) => (
              <Reveal key={r.t} delay={i * 0.06} className="grid grid-cols-12 items-baseline gap-4 border-b border-ink/15 py-5">
                <li className="contents">
                  <span className="col-span-2 font-serif text-sm text-teal md:col-span-1">0{i + 1}</span>
                  <span className="col-span-10 font-serif text-2xl md:col-span-5 md:text-3xl">{r.t}</span>
                  <span className="col-span-10 col-start-3 text-sm text-ink/70 md:col-span-6 md:col-start-auto">{r.d}</span>
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section className="border-t border-ink/10 px-5 py-24 sm:px-10 md:py-36 lg:px-16" data-testid="studio-approach">
        <div className="grid grid-cols-12 gap-y-14 lg:gap-x-10">
          <div className="col-span-12 lg:col-span-5">
            <Reveal><Eyebrow n="04">Approccio</Eyebrow></Reveal>
            <Lines lines={["Il nostro modo", "di lavorare"]} className="mt-8 text-4xl leading-[1.1] sm:text-5xl" />
          </div>
          <div className="col-span-12 lg:col-span-6 lg:col-start-7">
            <Reveal>
              <p className="font-serif text-2xl leading-snug md:text-3xl">Non partiamo mai da un calendario da riempire.</p>
              <p className="mt-6 text-base leading-relaxed text-ink/80 md:text-lg">
                Partiamo dall'ascolto, dall'analisi e dalla comprensione di ciò che rende un'attività diversa dalle altre. Studiamo una direzione, progettiamo i contenuti, coordiniamo la produzione e seguiamo la comunicazione nel tempo.
              </p>
            </Reveal>
          </div>
        </div>
        <Reveal className="mt-20 border-t border-ink/15 pt-12 md:mt-28">
          <p className="max-w-5xl font-serif text-3xl leading-[1.2] md:text-5xl" data-testid="studio-claim">
            <span className="text-ink/55">Perché un contenuto può attirare l'attenzione. </span>
            <strong className="font-normal text-teal">Ma è una strategia coerente a costruire un brand riconoscibile.</strong>
          </p>
        </Reveal>
      </section>

      <section className="bg-teal px-5 py-24 text-mint sm:px-10 md:py-32 lg:px-16" data-testid="studio-areas">
        <div className="grid grid-cols-12 gap-y-12 lg:gap-x-10">
          <div className="col-span-12 lg:col-span-5">
            <Reveal>
              <p className="eyebrow flex items-center gap-3 !text-mint"><span className="font-serif text-sm tracking-normal">05</span><span className="h-px w-8 bg-mint/50" aria-hidden="true" />Dove operiamo</p>
            </Reveal>
            <Lines lines={["Radicati in Lombardia,", "vicini al territorio."]} className="mt-8 text-4xl leading-[1.1] sm:text-5xl" />
            <Reveal delay={0.15}>
              <p className="mt-8 max-w-md text-base leading-relaxed text-mint/85 md:text-lg">
                Affianchiamo attività e professionisti tra le province di Como, Monza e Brianza, Varese e Lecco: conosciamo il territorio, il suo pubblico e le realtà che lo animano. Possiamo realizzare shooting e riprese direttamente nella tua attività.
              </p>
            </Reveal>
          </div>
          <ul className="col-span-12 lg:col-span-6 lg:col-start-7">
            {AREAS.map((a, i) => (
              <Reveal key={a} delay={i * 0.08} className="flex items-baseline justify-between border-t border-mint/25 py-6 md:py-8">
                <li className="contents">
                  <span className="font-serif text-3xl md:text-5xl">{a}</span>
                  <span className="text-xs uppercase tracking-[0.2em] text-mint/80">Provincia</span>
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>
      <ContactCta />
    </>
  );
}
